import {Injectable} from "@angular/core";
import {FormService, ValidationMsg} from "../../../../umbrella-common/services/form.service";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {DAILY_RATES_ONLY, Fee, FeeType, Order, Product, Risk, StandardName} from "../../../../store/state/order.state";
import {IntermediatePriceAdjustment, IntermediatePriceAdjustmentType, PriceAdjustmentsConfig} from "./pricing-adjustments.component";
import {distinctUntilChanged, filter, skip, startWith, takeWhile} from "rxjs/operators";
import {AppState} from "../../../../store/state/app.state";
import {Action, ActionCreator, Store} from "@ngrx/store";
import {ReviewerActions} from "../../../../store/actions/reviewer.actions";
import {OrderActions} from "../../../../store/actions/order.actions";
import {BaseOrderFormService} from "../../common/base-order-form.service";
import {TranslateService} from "@ngx-translate/core";
import {FeeConfig, FeeMode} from "../../../../configuration/configuration.model";
import {Actions, ofType} from "@ngrx/effects";

@Injectable()
export class PriceAdjustmentsFormService extends FormService implements BaseOrderFormService {

	private intermediatePriceAdjustments: { [standardName: string]: IntermediatePriceAdjustment[] } = {};
	private currentProducts: { [standardName: string]: Product } = {};
	private actionCreators: { [type: string]: ActionCreator<string, (props: { standardName: StandardName, value: Risk }) => { standardName: StandardName, value: Risk } & Action> } = {
		'RISK': ReviewerActions.SET_OVERRIDDEN_RISK,
		'IT_COMPLEXITY': ReviewerActions.SET_IT_COMPLEXITY,
		'BUSINESS_COMPLEXITY': ReviewerActions.SET_BUSINESS_COMPLEXITY,
		'AUDIT_EFFORT': ReviewerActions.SET_AUDIT_EFFORT,
		'COMPLEXITY_FACTOR': ReviewerActions.SET_COMPLEXITY_FACTOR,
	};

	constructor(private store: Store<AppState>, translate: TranslateService, private actions$: Actions) {
		super(translate);
	}

	public updateCurrentProduct(product: Product): void {
		this.currentProducts[product.standard.name] = product;
	}

	protected buildFormFor(formKey: string, [product, intermediatePriceAdjustments, config]: [Product, IntermediatePriceAdjustment[], PriceAdjustmentsConfig]) {
		this.intermediatePriceAdjustments[formKey] = intermediatePriceAdjustments;
		this.currentProducts[formKey] = product;
		const form = new FormGroup({});

		this.buildIntermediateAdjustmentControls(intermediatePriceAdjustments, product, form);

		this.buildFeesModeControl(product, form);
		this.buildDailyRateControl(product, form, config);

		const modeControl = form.get(this.feeFormControl(product.standard.name, 'fee-modes'));
		modeControl.valueChanges.pipe(startWith(modeControl.value), distinctUntilChanged(), skip(1)).subscribe((value: FeeMode) => {
			if (value === FeeMode.PER_YEAR) {
				this.buildDailyRateControl(product, form, config);
			} else {
				form.removeControl(this.dailyRateFormControl(product.standard.name));
			}
		})
		this.buildFeesControls(product, form, config);

		this.actions$.pipe(
			ofType(OrderActions.SET_PRE_AUDIT_REQUESTED),
			filter(action => action.standardName === product.standard.name)
		).subscribe(action => {
			const feeConfig = config.fees.find(fee => fee.type === FeeType.PRE_AUDIT_FEE);
			if (!feeConfig) {
				return;
			}

			const controlName = this.feeFormControl(product.standard.name, feeConfig.type);
			const modeControl = form.get(this.feeFormControl(product.standard.name, 'fee-modes'));
			const currentProduct = this.currentProducts[product.standard.name];

			if (action.value && modeControl.value === feeConfig.mode) {
				if (!form.contains(controlName)) {
					this.buildCertFeeControlForForm(currentProduct, form, feeConfig);
				}
				this.store.dispatch(OrderActions.SET_FEE({standard: product.standard.name, fee: {mode: feeConfig.mode, type: feeConfig.type, applied: true, amount: this.getFeeAmountFor(currentProduct, feeConfig.type), applicationPolicy: feeConfig.applicationPolicy}}));
			} else {
				form.removeControl(controlName);
				this.store.dispatch(OrderActions.SET_FEE({standard: product.standard.name, fee: {mode: feeConfig.mode, type: feeConfig.type, applied: false, amount: this.getFeeAmountFor(currentProduct, feeConfig.type), applicationPolicy: feeConfig.applicationPolicy}}));
			}
		});

		return form;
	}

	getControlNamesInOrder(): string[] {
		return [];
	}

	protected override getFormSpecificErrorMap(): { [p: string]: ValidationMsg[] } {
		return {
			pattern: [{msg: 'One decimal space only'}]
		};
	}

	getControlNameFor(intermediatePriceAdjustment: IntermediatePriceAdjustment, standard: StandardName) {
		return intermediatePriceAdjustment.type.toLowerCase().concat(standard);
	}

	dailyRateFormControl(standard: StandardName) {
		return 'dailyRate'.concat(standard);
	}

	feeFormControl(standard: StandardName, feeName: string) {
		return feeName.concat(standard);
		// return 'fee-control-' + feeName.concat(standard);

	}

	getIntermediateAdjustmentValue(product: Product, type: IntermediatePriceAdjustmentType) {
		switch (type) {
			case IntermediatePriceAdjustmentType.RISK:
				return product.pricing.pricingAdjustments.risk != null ? product.pricing.pricingAdjustments.risk : Risk.NO_RISK;
			case IntermediatePriceAdjustmentType.IT_COMPLEXITY:
				return product.pricing.pricingAdjustments.itComplexity != null ? product.pricing.pricingAdjustments.itComplexity : Risk.NO_RISK;
			case IntermediatePriceAdjustmentType.BUSINESS_COMPLEXITY:
				return product.pricing.pricingAdjustments.businessComplexity != null ? product.pricing.pricingAdjustments.businessComplexity : Risk.NO_RISK;
			case IntermediatePriceAdjustmentType.AUDIT_EFFORT:
				return product.pricing.pricingAdjustments.auditEffort != null ? product.pricing.pricingAdjustments.auditEffort : Risk.NO_RISK;
			case IntermediatePriceAdjustmentType.COMPLEXITY_FACTOR:
				return product.pricing.pricingAdjustments.complexityFactor != null ? product.pricing.pricingAdjustments.complexityFactor : Risk.NO_RISK;
			default:
				return null;
		}
	}

	fillOrder(order: Order): Order {
		// order is taken from store in effects
		return order;
	}

	private setIntermediatePriceAdjustmentValue(product: Product, type: IntermediatePriceAdjustmentType, value: Risk) {
		value = value == Risk.NO_RISK ? null : value;

		switch (type) {
			case IntermediatePriceAdjustmentType.RISK:
				product.pricing.pricingAdjustments.risk = value;
				break;
			case IntermediatePriceAdjustmentType.IT_COMPLEXITY:
				product.pricing.pricingAdjustments.itComplexity = value;
				break;
			case IntermediatePriceAdjustmentType.BUSINESS_COMPLEXITY:
				product.pricing.pricingAdjustments.businessComplexity = value;
				break;
			case IntermediatePriceAdjustmentType.AUDIT_EFFORT:
				product.pricing.pricingAdjustments.auditEffort = value;
				break;
			case IntermediatePriceAdjustmentType.COMPLEXITY_FACTOR:
				product.pricing.pricingAdjustments.complexityFactor = value;
				break;
		}
	}

	private buildIntermediateAdjustmentControls(intermediatePriceAdjustments: IntermediatePriceAdjustment[], product: Product, form: FormGroup) {
		intermediatePriceAdjustments.forEach(pa => {
			const control = new FormControl(this.getIntermediateAdjustmentValue(product, pa.type), [Validators.required]);
			form.addControl(this.getControlNameFor(pa, product.standard.name), control);
			this.initIntermediatePriceAdjustmentControl(control, pa.type, product);
		});
	}

	private buildDailyRateControl(product: Product, form: FormGroup, config: PriceAdjustmentsConfig) {
		const dailyRateControl = new FormControl(this.getDailyRate(product), [Validators.required, Validators.min(config.dailyRate.minAmount), Validators.max(config.dailyRate.maxAmount)]);
		form.addControl(this.dailyRateFormControl(product.standard.name), dailyRateControl);
		dailyRateControl.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)), distinctUntilChanged())
			.subscribe(dailyRate => {
				const shouldClearOverriddenDailyRate = dailyRate == product.pricing.dailyRate;
				this.store.dispatch(OrderActions.SET_DAILY_RATE({
					standard: product.standard.name,
					dailyRate: shouldClearOverriddenDailyRate ? null : dailyRate
				}));
			});
	}

	public buildFeesControls(product: Product, form: FormGroup, config: PriceAdjustmentsConfig) {
		const standardName = product.standard.name;
		const modeControl = form.get(this.feeFormControl(standardName, 'fee-modes'))!;

		const rebuildForMode = (mode: FeeMode) => {
			const currentProduct = this.currentProducts[standardName];
			config.fees.forEach((fee: FeeConfig) => {
				form.removeControl(this.feeFormControl(standardName, fee.type));
				this.store.dispatch(OrderActions.SET_FEE({standard: currentProduct.standard.name, fee: {mode: fee.mode, type: fee.type, applied: false, amount: this.getFeeAmountFor(currentProduct, fee.type), applicationPolicy: fee.applicationPolicy}}));
			});

			const isTransfer = !!product.transferQuestions?.lastAuditEvent
			config.fees
				.filter(fee => fee.mode === mode)
				.filter(fee => !fee.excludeForProducts.includes(standardName))
				.filter(fee => !(fee.excludeForTransfer && isTransfer))
				.filter(fee => this.showPreAudit(fee, currentProduct))
				.filter(fee => this.showRecertificationFee(fee, currentProduct))
				.forEach(fee => {
					this.buildCertFeeControlForForm(currentProduct, form, fee);
					this.store.dispatch(OrderActions.SET_FEE({standard: currentProduct.standard.name, fee: {mode: fee.mode, type: fee.type, applied: true, amount: this.getFeeAmountFor(currentProduct, fee.type), applicationPolicy: fee.applicationPolicy}}));
				});
		};

		rebuildForMode(modeControl.value);
		modeControl.valueChanges.pipe(startWith(modeControl.value), distinctUntilChanged(), skip(1)).subscribe(mode => {
			this.resetFeesForOtherMode(this.currentProducts[standardName], mode);
			rebuildForMode(mode);
		});
	}

	private showPreAudit(fee: FeeConfig, currentProduct: Product) {
		return !(fee.type === FeeType.PRE_AUDIT_FEE && !currentProduct.additionalQuestions.initialCertificationQuestions?.preAuditRequested);
	}

	private showRecertificationFee(fee: FeeConfig, currentProduct: Product) {
		return !(fee.type === FeeType.RECERTIFICATION_DAILY_RATE && !currentProduct.transferQuestions?.lastAuditEvent);
	}

	private resetFeesForOtherMode(product: Product, newMode: FeeMode) {
		product.pricing.pricingAdjustments.fees
			.filter(fee => fee.mode !== newMode && fee.applied)
			.forEach(fee => {
				this.store.dispatch(OrderActions.SET_FEE({
					standard: product.standard.name,
					fee: {...fee, applied: false}
				}));
			});
	}

	private buildCertFeeControlForForm(product: Product, form: FormGroup, feeConfig: FeeConfig) {
		const feeControl = new FormControl(this.getFeeAmountFor(product, feeConfig.type),
			[Validators.required, Validators.min(feeConfig.minAmount), Validators.max(feeConfig.maxAmount)]
		);
		form.addControl(this.feeFormControl(product.standard.name, feeConfig.type), feeControl);

		feeControl.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)), distinctUntilChanged())
			.subscribe(feeAmount => {
				const modeControl = form.get(this.feeFormControl(product.standard.name, 'fee-modes'))!;

				this.store.dispatch(OrderActions.SET_FEE({
					standard: product.standard.name,
					fee: {
						type: feeConfig.type,
						amount: feeAmount,
						applied: true,
						mode: modeControl.value,
						applicationPolicy: feeConfig.applicationPolicy
					}
				}));
			});
	}

	private buildFeesModeControl(product: Product, form: FormGroup<{}>) {
		const fees = product.pricing.pricingAdjustments.fees;
		const mode = fees.some(fee => fee.mode === FeeMode.PER_EVENT && fee.applied) ? FeeMode.PER_EVENT : FeeMode.PER_YEAR;
		const feeModeControl = new FormControl(mode);
		form.addControl(this.feeFormControl(product.standard.name, 'fee-modes'), feeModeControl);
	}

	private initIntermediatePriceAdjustmentControl(control: FormControl, type: IntermediatePriceAdjustmentType, product: Product) {
		control.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)), distinctUntilChanged()).subscribe((value: Risk) => {
			this.store.dispatch(this.actionCreators[type]({standardName: product.standard.name, value}));
		});
	}

	private getDailyRate(product: Product): number {
		return product.pricing.pricingAdjustments.dailyRate || product.pricing.dailyRate;
	}

	private getFeeAmountFor(product: Product, feeType: FeeType): number {
		const dailyRate = product.pricing.pricingAdjustments.fees?.find(fee => fee.type === feeType)?.amount
			|| this.getCurrentCertFeeAmount(product, feeType)
			|| (DAILY_RATES_ONLY.has(feeType) ? product.pricing.dailyRate : null);

		if (dailyRate == null) {
			if ([FeeType.CERTIFICATION_FEE_YEAR_1, FeeType.CERTIFICATION_FEE_YEAR_2, FeeType.CERTIFICATION_FEE_YEAR_3, FeeType.CERTIFICATION_FEE_PER_EVENT].includes(feeType)) {
				return this.getCurrentCertFeeAmount(product, FeeType.CERTIFICATION_FEE);
			}
			if ([FeeType.ADMIN_FEE_YEAR_1, FeeType.ADMIN_FEE_YEAR_2, FeeType.ADMIN_FEE_YEAR_3].includes(feeType)) {
				return this.getCurrentCertFeeAmount(product, FeeType.ADMINISTRATION_FEE);
			}
			if (FeeType.REGISTRATION_FEE_PER_EVENT === feeType) {
				return this.getCurrentCertFeeAmount(product, FeeType.REGISTRATION_FEE);
			}
		}

		return dailyRate;
	}

	private getCurrentFeeFor(product: Product, feeType: FeeType): Fee[] {
		return product.pricing.annualPricings
			.flatMap(ap => ap.auditPricings)
			.flatMap(ap => ap.fees)
			.filter(fee => fee.type === feeType)
	}

	private getCurrentCertFeeAmount(product: Product, feeType: FeeType): number {
		return this.getCurrentFeeFor(product, feeType).map(fee => fee.amount)[0];
	}
}
